import {AnimatePresence, motion} from 'framer-motion';
import {EventAnalytics, Palette} from 'hubbl-shared';
import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {ApiEventAnalytics} from '../../api/collections/ApiEventAnalytics';
import {Api} from '../../api';
import {useAppSelector, useI18n} from '../../hooks';
import {AdminWebRoute} from '../../routes/AdminWebRoute';
import {RootRoute} from '../../routes/RootRoute';
import {UserSelector} from '../../store/user/UserSelector';
import {PrimaryButton} from '../buttons/primary-button/PrimaryButton';
import {TextHeading3, TextHeading5, TextHeading6, TextHeading7} from '../index';

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SummaryCard = styled.div<{color: Palette}>`
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-left: 4px solid ${props => props.color || Palette.primaryTerraCotta};
  background: ${Palette.white};
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CardTitle = TextHeading5;

const CardIcon = styled.div`
  font-size: 24px;
  opacity: 0.8;
`;

const CardValue = styled(TextHeading3)`
  color: ${Palette.almostBlack};
  margin-bottom: 8px;
`;

const CardSubtext = TextHeading6;

const TimeframeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${Palette.almostBlackTransparent5};
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: ${Palette.almostBlack500};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TimeframeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${Palette.almostBlack500};
`;

const CardTrend = styled.div<{positive?: boolean}>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${props =>
    props.positive ? Palette.primaryPadua : Palette.primaryTerraCotta};
`;

const LoadingCard = styled(SummaryCard)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`;

const EmptyStateCTACard = styled(SummaryCard)`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const EmptyStateInfoCard = styled(SummaryCard)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  min-height: 140px;
  overflow: hidden;
`;

const TipContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const TipIcon = styled.div`
  font-size: 24px;
  opacity: 0.8;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 8px;
`;

interface EventAnalyticsSummaryCardsProps {
  onCardClick?: (cardType: string) => void;
}

const TIP_ROTATION_INTERVAL = 5000; // 5 seconds

export const EventAnalyticsSummaryCards = memo(
  ({onCardClick}: EventAnalyticsSummaryCardsProps) => {
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const navigate = useNavigate();
    const t = useI18n();
    const [summary, setSummary] = useState<
      | (EventAnalytics.EventAnalyticsSummary & {
          timeframeLabel?: string;
          startDate?: string;
          endDate?: string;
        })
      | null
    >(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [hasEvents, setHasEvents] = useState<boolean | null>(null);

    const tips = useMemo(
      () => [
        {
          icon: '🔔',
          title: t.dashboard.eventTipsTitle,
          description: t.dashboard.eventTip1,
        },
        {
          icon: '📝',
          title: t.dashboard.eventTipsTitle,
          description: t.dashboard.eventTip2,
        },
        {
          icon: '✨',
          title: t.dashboard.eventTipsTitle,
          description: t.dashboard.eventTip3,
        },
        {
          icon: '📱',
          title: t.dashboard.eventTipsTitle,
          description: t.dashboard.eventTip4,
        },
        {
          icon: '📧',
          title: t.dashboard.eventTipsTitle,
          description: t.dashboard.eventTip5,
        },
        {
          icon: '📊',
          title: t.dashboard.eventTipsTitle,
          description: t.dashboard.eventTip6,
        },
      ],
      [t],
    );

    // Rotate tips automatically
    useEffect(() => {
      if (summary === null || summary.totalEvents === 0) {
        const interval = setInterval(() => {
          setCurrentTipIndex(prev => (prev + 1) % tips.length);
        }, TIP_ROTATION_INTERVAL);

        return () => clearInterval(interval);
      }
    }, [summary, tips.length]);

    const formatDateRange = useCallback(
      (startDate: string, endDate: string) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        const startMonth = start.toLocaleDateString('en-US', {month: 'short'});
        const endMonth = end.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        });

        return `${startMonth} - ${endMonth}`;
      },
      [],
    );

    const loadSummary = useCallback(async () => {
      if (!userDetails?.companyId) return;

      try {
        setLoading(true);
        setError(null);
        const [summaryData, eventsCount] = await Promise.all([
          ApiEventAnalytics.getEventAnalyticsSummary(userDetails.companyId),
          Api.event.getEventsCount(userDetails.companyId),
        ]);
        setSummary(summaryData);
        // Check if events exist directly from the events collection
        setHasEvents(eventsCount > 0);
      } catch (err) {
        setError(t.analytics.failedToLoadEventAnalytics);
        console.error('Error loading event analytics summary:', err);
        // Try to check events count even if analytics fails
        try {
          const eventsCount = await Api.event.getEventsCount(
            userDetails.companyId,
          );
          setHasEvents(eventsCount > 0);
        } catch (eventsErr) {
          console.error('Error checking events count:', eventsErr);
          setHasEvents(null);
        }
      } finally {
        setLoading(false);
      }
    }, [userDetails?.companyId, t]);

    useEffect(() => {
      loadSummary();
    }, [loadSummary]);

    const handleCardClick = useCallback(
      (cardType: string) => {
        if (onCardClick) {
          onCardClick(cardType);
        }
      },
      [onCardClick],
    );

    if (loading) {
      return (
        <CardsContainer>
          {Array.from({length: 4}).map((_, index) => (
            <LoadingCard key={index} color={Palette.almostBlack}>
              <TextHeading7 text={t.common.loading} />
            </LoadingCard>
          ))}
        </CardsContainer>
      );
    }

    // Show empty state only if we've confirmed no events exist
    // If events exist but analytics are missing, we should show analytics cards (or loading)
    const shouldShowEmptyState = hasEvents === false;

    // If we have events but no analytics summary, show loading or a message
    if (hasEvents === true && !summary && !error) {
      return (
        <>
          <SectionHeader>
            <TextHeading3 text={t.analytics.eventAnalytics} />
          </SectionHeader>
          <CardsContainer>
            {Array.from({length: 4}).map((_, index) => (
              <LoadingCard key={index} color={Palette.almostBlack}>
                <TextHeading7 text={t.common.loading} />
              </LoadingCard>
            ))}
          </CardsContainer>
        </>
      );
    }

    if (error || shouldShowEmptyState) {
      const handleCreateEvent = () => {
        navigate(`${RootRoute.AuthAdminHome}${AdminWebRoute.CreateEvent}`);
      };

      return (
        <>
          <SectionHeader>
            <TextHeading3 text={t.analytics.eventAnalytics} />
          </SectionHeader>
          <CardsContainer>
            <EmptyStateCTACard color={Palette.primaryTerraCotta}>
              <EmptyStateIcon>📅</EmptyStateIcon>
              <CardValue text={t.dashboard.createYourFirstEvent} />
              <CardSubtext text={t.dashboard.createFirstEventDescription} />
              <PrimaryButton
                label={t.dashboard.createYourFirstEvent}
                onClick={handleCreateEvent}
              />
            </EmptyStateCTACard>

            <EmptyStateInfoCard color={Palette.primaryDustySky}>
              <AnimatePresence mode="wait">
                <TipContent
                  key={currentTipIndex}
                  initial={{opacity: 0, y: 10}}
                  animate={{opacity: 1, y: 0}}
                  exit={{opacity: 0, y: -10}}
                  transition={{duration: 0.4, ease: 'easeInOut'}}>
                  <CardHeader>
                    <CardTitle text={tips[currentTipIndex].title} />
                    <TipIcon>{tips[currentTipIndex].icon}</TipIcon>
                  </CardHeader>
                  <CardSubtext text={tips[currentTipIndex].description} />
                </TipContent>
              </AnimatePresence>
            </EmptyStateInfoCard>

            <EmptyStateInfoCard color={Palette.primaryPadua}>
              <CardHeader>
                <CardTitle text={t.dashboard.noEventsYet} />
                <CardIcon>📊</CardIcon>
              </CardHeader>
              <CardSubtext text={t.dashboard.noEventsDescription} />
            </EmptyStateInfoCard>
          </CardsContainer>
        </>
      );
    }

    // At this point, we should have summary data (or we've handled the null case above)
    if (!summary) {
      return (
        <>
          <SectionHeader>
            <TextHeading3 text={t.analytics.eventAnalytics} />
          </SectionHeader>
          <CardsContainer>
            {Array.from({length: 4}).map((_, index) => (
              <LoadingCard key={index} color={Palette.almostBlack}>
                <TextHeading7 text={t.common.loading} />
              </LoadingCard>
            ))}
          </CardsContainer>
        </>
      );
    }

    return (
      <>
        <SectionHeader>
          <TextHeading3 text={t.analytics.eventAnalytics} />
          <TimeframeInfo>
            <TimeframeBadge>
              📊 {summary.timeframeLabel || t.analytics.last3Months}
            </TimeframeBadge>
            <span>
              {summary.startDate && summary.endDate
                ? formatDateRange(summary.startDate, summary.endDate)
                : t.analytics.currentPeriod}
            </span>
          </TimeframeInfo>
        </SectionHeader>

        <CardsContainer>
          {/* Total Events Card */}
          <SummaryCard
            color={Palette.primaryTerraCotta}
            onClick={() => handleCardClick('total-events')}>
            <CardHeader>
              <CardTitle text={t.analytics.totalEvents} />
              <CardIcon>📅</CardIcon>
            </CardHeader>
            <CardValue text={summary.totalEvents.toString()} />
            <CardSubtext text={t.analytics.eventsCreatedInPeriod} />
            <CardTrend positive={summary.totalEvents > 0}>
              <span>📈</span>
              <span>{t.common.active}</span>
            </CardTrend>
          </SummaryCard>

          {/* Response Rate Card */}
          <SummaryCard
            color={Palette.primaryDustySky}
            onClick={() => handleCardClick('response-rate')}>
            <CardHeader>
              <CardTitle text={t.analytics.responseRate} />
              <CardIcon>📊</CardIcon>
            </CardHeader>
            <CardValue text={`${summary.averageResponseRate.toFixed(1)}%`} />
            <CardSubtext text={t.analytics.averageResponseRate} />
            <CardTrend positive={summary.averageResponseRate > 70}>
              <span>{summary.averageResponseRate > 70 ? '📈' : '📉'}</span>
              <span>
                {summary.averageResponseRate > 70
                  ? t.analytics.excellent
                  : t.analytics.needsImprovement}
              </span>
            </CardTrend>
          </SummaryCard>

          {/* Top Event Card */}
          <SummaryCard
            color={Palette.primaryPadua}
            onClick={() => handleCardClick('top-event')}>
            <CardHeader>
              <CardTitle text={t.analytics.topEvent} />
              <CardIcon>🏆</CardIcon>
            </CardHeader>
            {summary.topPerformingEvent ? (
              <>
                <CardValue text={summary.topPerformingEvent.eventTitle} />
                <CardSubtext
                  text={`${summary.topPerformingEvent.responseRate.toFixed(1)}% ${t.analytics.responseRate.toLowerCase()}`}
                />
                <CardTrend positive={true}>
                  <span>⭐</span>
                  <span>{t.analytics.bestPerformance}</span>
                </CardTrend>
              </>
            ) : (
              <>
                <CardValue text={t.analytics.noEvents} />
                <CardSubtext text={t.analytics.noEventsToDisplay} />
              </>
            )}
          </SummaryCard>

          {/* Evaluation Rate Card */}
          <SummaryCard
            color={Palette.primaryBianca}
            onClick={() => handleCardClick('evaluation-rate')}>
            <CardHeader>
              <CardTitle text={t.analytics.evaluationRate} />
              <CardIcon>📝</CardIcon>
            </CardHeader>
            <CardValue
              text={`${summary.evaluationCompletionRate.toFixed(1)}%`}
            />
            <CardSubtext text={t.analytics.evaluationCompletionRate} />
            <CardTrend positive={summary.evaluationCompletionRate > 60}>
              <span>{summary.evaluationCompletionRate > 60 ? '📈' : '📉'}</span>
              <span>
                {summary.evaluationCompletionRate > 60
                  ? t.analytics.good
                  : t.analytics.low}
              </span>
            </CardTrend>
          </SummaryCard>

          {/* Upcoming Events Card */}
          <SummaryCard
            color={Palette.primaryCherokee}
            onClick={() => handleCardClick('upcoming-events')}>
            <CardHeader>
              <CardTitle text={t.analytics.upcomingEvents} />
              <CardIcon>🔮</CardIcon>
            </CardHeader>
            <CardValue text={summary.upcomingEvents.toString()} />
            <CardSubtext text={t.analytics.eventsScheduledAhead} />
            <CardTrend positive={summary.upcomingEvents > 0}>
              <span>📅</span>
              <span>{t.analytics.scheduled}</span>
            </CardTrend>
          </SummaryCard>
        </CardsContainer>
      </>
    );
  },
);
